import React, { useEffect, useId, useState } from "react";
import {
  Alert, Box, Button, FormControl, FormHelperText, Grid2, IconButton, InputAdornment, InputLabel,
  MenuItem, Modal, OutlinedInput, Select, TextField, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { useAuth } from "../Auth/AuthContext";
import { Link, useNavigate } from "react-router-dom";

const API = "https://api.sigmasystems.com.br/api/ag-barber";

const style = {
  position: "absolute",
  top:  { xs: "50%", md: "50%" },
  left: { xs: "50%", md: "50%" },
  transform: { xs: "translate(-50%, -50%)", md: "translate(-50%, -50%)" },
  width:     { xs: "95vw", md: 815 },
  height:    { xs: "auto", md: "auto" },
  maxHeight: { xs: "95vh", md: "90vh" },
  overflowY: "auto",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  border: { xs: "none", md: "2px solid #4A7011" },
  borderRadius: { xs: 0, md: "8px" },
  boxShadow: 24,
  p: { xs: 2, md: 4 },
};

const btnPrimario = {
  color: "#fff", height: 40, width: 110, borderRadius: "5px", fontSize: "15px", fontWeight: "bold",
  backgroundColor: "#4a7011", textTransform: "none", "&:hover": { backgroundColor: "#293D09" },
};
const btnSecundario = {
  color: "#4a7011", height: 40, width: 110, border: "solid 2px #4a7011", borderRadius: "5px",
  fontSize: "15px", fontWeight: "bold", backgroundColor: "transparent", textTransform: "none",
  "&:hover": { backgroundColor: "#eeeeee" },
};

/* ---------- constantes e helpers ---------- */

const DIAS = [
  { v: 0, l: "Dom" }, { v: 1, l: "Seg" }, { v: 2, l: "Ter" }, { v: 3, l: "Qua" },
  { v: 4, l: "Qui" }, { v: 5, l: "Sex" }, { v: 6, l: "Sáb" },
];
const NOMES_DIA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const CARGOS = ["Barbeiro"];   // acrescente outros cargos aqui

// Todo dia atendido tem dois períodos: manhã e tarde (ex.: 9h–12h e 13:30–20h)
const HORARIO_VAZIO = { manha_start: null, manha_end: null, tarde_start: null, tarde_end: null };
const DIAS_PADRAO = [1, 2, 3, 4, 5, 6];   // segunda a sábado (domingo é só marcar o botão "Dom")

const FORM_PROF_VAZIO = {
  nome: "", cargo: "", email: "", pwd: "", type: "", comission: "",
  servicos: [],
  work_days: DIAS_PADRAO,
  horarios: Object.fromEntries(DIAS_PADRAO.map((d) => [d, HORARIO_VAZIO])),   // { [dia]: horário }
};
const FORM_SERV_VAZIO = { name: "", timer: null, price: "" };
const FORM_PROD_VAZIO = { name: "", price: "" };

// horário do dia -> "HH:mm:ss" (horário local, sem conversão de fuso)
const hhmmss = (d) => (d && d.isValid() ? d.format("HH:mm:ss") : null);
const minutos = (d) => d.hour() * 60 + d.minute();
const parsePreco = (v) => Number(String(v).trim().replace(",", "."));
const mensagemErro = (error, padrao) => error.response?.data?.message || padrao;
const precoParaCampo = (v) => String(Number(v)).replace(".", ",");   // 35.5 -> "35,5"

// "09:00:00" (vindo do banco) -> Dayjs que o TimeField entende
const horaParaDayjs = (t) => dayjs(`2000-01-01T${t.length === 5 ? `${t}:00` : t}`);

const formatarDuracao = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};
const formatarPreco = (v) =>
  Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Resposta do get-user -> formulário do modal.
// O banco guarda só linhas (dia, início, fim): com 2 linhas no dia, a 1ª é a manhã e a 2ª a tarde;
// com 1 linha, vale como manhã se começar antes do meio-dia e como tarde se começar depois.
function formDoUsuario(u) {
  const porDia = {};
  for (const r of u.schedule) (porDia[r.weekday] ??= []).push(r);

  const horarios = {};
  for (const [dia, linhas] of Object.entries(porDia)) {
    linhas.sort((a, b) => a.start_time.localeCompare(b.start_time));
    const h = { ...HORARIO_VAZIO };
    if (linhas.length >= 2) {
      h.manha_start = horaParaDayjs(linhas[0].start_time);
      h.manha_end = horaParaDayjs(linhas[0].end_time);
      h.tarde_start = horaParaDayjs(linhas[1].start_time);
      h.tarde_end = horaParaDayjs(linhas[1].end_time);
    } else {
      const [r] = linhas;
      const periodo = Number(r.start_time.slice(0, 2)) < 12 ? "manha" : "tarde";
      h[`${periodo}_start`] = horaParaDayjs(r.start_time);
      h[`${periodo}_end`] = horaParaDayjs(r.end_time);
    }
    horarios[dia] = h;
  }

  return {
    nome: u.name,
    cargo: u.role ?? "",
    email: u.email,
    pwd: "",   // vazio = manter a senha atual
    type: u.type,
    comission: String(Number(u.commission)),
    servicos: u.services,
    work_days: Object.keys(horarios).map(Number),
    horarios,
  };
}

// Lê um período (início + término). Vazio é permitido.
// Devolve { erro } ou { periodo: null | { start, end, ini, fim } }
function lerPeriodo(ini, fim, nome) {
  if (!ini && !fim) return { periodo: null };
  if (!ini || !fim) return { erro: `informe início e término da ${nome}.` };
  if (!ini.isValid() || !fim.isValid()) return { erro: `horário da ${nome} inválido.` };
  if (minutos(ini) >= minutos(fim)) return { erro: `na ${nome}, o término deve ser depois do início.` };
  return { periodo: { start: hhmmss(ini), end: hhmmss(fim), ini: minutos(ini), fim: minutos(fim) } };
}
const paraPayload = (p) => (p ? { start: p.start, end: p.end } : null);

// Devolve { erro } ou { schedule } no formato que a API espera.
// Na edição, a senha é opcional (em branco = manter a atual).
function validarProf(f, editando) {
  if (!f.nome.trim()) return { erro: "Informe o nome." };
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return { erro: "Informe um email válido." };
  if ((!editando || f.pwd) && f.pwd.length < 8) return { erro: "A senha deve ter pelo menos 8 caracteres." };
  const comissao = Number(f.comission || 0);
  if (!(comissao >= 0 && comissao <= 100)) return { erro: "A comissão deve estar entre 0 e 100." };
  if (f.work_days.length === 0) return { erro: "Selecione pelo menos um dia de atendimento." };

  const schedule = [];
  for (const dia of [...f.work_days].sort((a, b) => a - b)) {
    const h = f.horarios[dia] ?? HORARIO_VAZIO;
    const manha = lerPeriodo(h.manha_start, h.manha_end, "manhã");
    const tarde = lerPeriodo(h.tarde_start, h.tarde_end, "tarde");

    const erroPeriodo = manha.erro || tarde.erro;
    if (erroPeriodo) return { erro: `${NOMES_DIA[dia]}: ${erroPeriodo}` };
    if (!manha.periodo && !tarde.periodo)
      return { erro: `${NOMES_DIA[dia]}: informe pelo menos um período.` };
    if (manha.periodo && tarde.periodo && manha.periodo.fim > tarde.periodo.ini)
      return { erro: `${NOMES_DIA[dia]}: a tarde deve começar depois do término da manhã.` };

    schedule.push({
      weekday: dia,
      morning: paraPayload(manha.periodo),
      afternoon: paraPayload(tarde.periodo),
    });
  }
  return { schedule };
}

function validarServ(f) {
  if (!f.name.trim()) return "Informe o nome do serviço.";
  if (!f.timer || !f.timer.isValid() || minutos(f.timer) <= 0) return "Informe o tempo do serviço.";
  if (String(f.price).trim() === "" || !(parsePreco(f.price) >= 0)) return "Informe um valor válido.";
  return null;
}

function validarProd(f) {
  if (!f.name.trim()) return "Informe o nome do produto.";
  if (f.name.trim().length > 45) return "O nome do produto pode ter no máximo 45 caracteres.";
  if (String(f.price).trim() === "" || !(parsePreco(f.price) >= 0)) return "Informe um valor válido.";
  return null;
}

const CampoHora = ({ label, value, onChange, size }) => (
  <TimeField label={label} format="HH:mm" ampm={false} fullWidth color="success" size={size}
    value={value} onChange={onChange} />
);

// Uma linha da grade de horários: dia + manhã (início/fim) + tarde (início/fim) + botão de copiar
const gradeLinha = {
  display: "grid",
  gridTemplateColumns: "44px repeat(4, 1fr) 36px",
  gap: 8,
  alignItems: "center",
};

const LinhaDia = ({ dia, horario, onChange, onCopiar }) => (
  <div style={{ ...gradeLinha, marginBottom: 8 }}>
    <strong style={{ fontSize: 14 }}>{DIAS[dia].l}</strong>
    <CampoHora size="small" label="Início" value={horario.manha_start} onChange={(v) => onChange(dia, "manha_start", v)} />
    <CampoHora size="small" label="Fim" value={horario.manha_end} onChange={(v) => onChange(dia, "manha_end", v)} />
    <CampoHora size="small" label="Início" value={horario.tarde_start} onChange={(v) => onChange(dia, "tarde_start", v)} />
    <CampoHora size="small" label="Fim" value={horario.tarde_end} onChange={(v) => onChange(dia, "tarde_end", v)} />
    <IconButton size="small" title="Copiar este horário para todos os dias selecionados"
      onClick={() => onCopiar(dia)}>
      <ContentCopy fontSize="small" />
    </IconButton>
  </div>
);

/* ---------- componente ---------- */

function Configuracao() {
  const { nome, user, logout, business_id } = useAuth();
  const navigate = useNavigate();
  const outlinedAmountId = useId();

  // Só ADMIN abre os modais de edição. A trava de verdade está no servidor (token); aqui é só a interface.
  const ehAdmin = (user ?? user) === "ADMIN";
  const clicavel = (acao) =>
    ehAdmin
      ? {
          onClick: acao,
          onKeyDown: (e) => e.key === "Enter" && acao(),
          role: "button",
          tabIndex: 0,
          title: "Clique para editar",
          style: { cursor: "pointer" },
        }
      : {};

  const [services, setServices] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [produtos, setProdutos] = useState([]);

  const [open, setOpen] = useState(false);
  const [openAddServ, setOpenAddServ] = useState(false);
  const [openAddProd, setOpenAddProd] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroProf, setErroProf] = useState("");
  const [erroServ, setErroServ] = useState("");
  const [erroProd, setErroProd] = useState("");

  // id do registro em edição (null = criando um novo)
  const [editProfId, setEditProfId] = useState(null);
  const [editServId, setEditServId] = useState(null);
  const [editProdId, setEditProdId] = useState(null);

  const [formAddProf, setFormAddProf] = useState(FORM_PROF_VAZIO);
  const [formAddServ, setFormAddServ] = useState(FORM_SERV_VAZIO);
  const [formAddProd, setFormAddProd] = useState(FORM_PROD_VAZIO);

  const handleChangeAddProf = (field, value) =>
    setFormAddProf((prev) => ({ ...prev, [field]: value }));
  const handleChangeAddServ = (field, value) =>
    setFormAddServ((prev) => ({ ...prev, [field]: value }));
  const handleChangeAddProd = (field, value) =>
    setFormAddProd((prev) => ({ ...prev, [field]: value }));

  /* ----- horários por dia ----- */

  // Ao marcar um dia novo, ele já vem com o horário de um dia selecionado (só ajusta o que muda)
  const handleDias = (_, dias) => {
    if (!dias) return;
    setFormAddProf((prev) => {
      const base = prev.work_days.map((d) => prev.horarios[d]).find(Boolean) ?? HORARIO_VAZIO;
      const horarios = { ...prev.horarios };
      dias.forEach((d) => { if (!horarios[d]) horarios[d] = { ...base }; });
      return { ...prev, work_days: dias, horarios };
    });
  };

  const handleChangeHorario = (dia, campo, valor) =>
    setFormAddProf((prev) => ({
      ...prev,
      horarios: { ...prev.horarios, [dia]: { ...prev.horarios[dia], [campo]: valor } },
    }));

  // Copia o horário de um dia para todos os dias selecionados
  const copiarParaTodos = (dia) =>
    setFormAddProf((prev) => {
      const modelo = prev.horarios[dia];
      const horarios = { ...prev.horarios };
      prev.work_days.forEach((d) => { horarios[d] = { ...modelo }; });
      return { ...prev, horarios };
    });

  /* ----- abrir / fechar modais ----- */

  // criar
  const handleOpenProf = () => { setFormAddProf(FORM_PROF_VAZIO); setEditProfId(null); setOpen(true); };
  const handleOpenServ = () => { setFormAddServ(FORM_SERV_VAZIO); setEditServId(null); setOpenAddServ(true); };
  const handleOpenProd = () => { setFormAddProd(FORM_PROD_VAZIO); setEditProdId(null); setOpenAddProd(true); };

  // editar (somente ADMIN)
  const abrirEdicaoProf = async (p) => {
    if (!ehAdmin) return;
    try {
      const { data } = await axios.post(`${API}/get-user`, { id: p.id, business_id });
      setFormAddProf(formDoUsuario(data));
      setEditProfId(p.id);
      setErroProf("");
      setShowPassword(false);
      setOpen(true);
    } catch (error) {
      window.alert(mensagemErro(error, "Não foi possível carregar o profissional."));
    }
  };

  const abrirEdicaoServ = (s) => {
    if (!ehAdmin) return;
    setFormAddServ({
      name: s.name,
      timer: dayjs("2000-01-01T00:00:00").add(s.timer, "minute"),   // 90 -> 01:30
      price: precoParaCampo(s.price),
    });
    setEditServId(s.id);
    setErroServ("");
    setOpenAddServ(true);
  };

  const abrirEdicaoProd = (p) => {
    if (!ehAdmin) return;
    setFormAddProd({ name: p.name, price: precoParaCampo(p.price) });
    setEditProdId(p.id);
    setErroProd("");
    setOpenAddProd(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenAddServ(false);
    setOpenAddProd(false);
    setEditProfId(null);
    setEditServId(null);
    setEditProdId(null);
    setErroProf("");
    setErroServ("");
    setErroProd("");
  };

  /* ----- buscar dados ----- */
  const getServicos = async () => {
    try {
      const { data } = await axios.post(`${API}/get-services`, { business_id });
      setServices(data);
    } catch (error) {
      console.error("Erro ao buscar serviços:", error);
    }
  };

  const getProfissionais = async () => {
    try {
      const { data } = await axios.post(`${API}/get-users`, { business_id });
      setProfissionais(data);
    } catch (error) {
      console.error("Erro ao buscar profissionais:", error);
    }
  };

  const getProdutos = async () => {
    try {
      const { data } = await axios.post(`${API}/get-products`, { business_id });
      setProdutos(data);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
    }
  };

  useEffect(() => {
    getServicos();
    getProfissionais();
    getProdutos();
  }, []);

  /* ----- salvar profissional (criar ou editar) ----- */
  const salvarProf = async () => {
    const editando = editProfId !== null;
    const { erro, schedule } = validarProf(formAddProf, editando);
    if (erro) {
      setErroProf(erro);
      return;
    }

    setSalvando(true);
    setErroProf("");
    const dados = {
      name: formAddProf.nome.trim(),
      email: formAddProf.email.trim().toLowerCase(),
      type: formAddProf.type || "EMPLOYEE",
      role: formAddProf.cargo,
      commission: Number(formAddProf.comission || 0),
      schedule,   // [{ weekday, morning: { start, end } | null, afternoon: { start, end } | null }]
      services: formAddProf.servicos,
    };

    try {
      if (editando) {
        await axios.post(`${API}/update-user`, {
          id: editProfId,
          formEditUser: formAddProf.pwd ? { ...dados, pwd: formAddProf.pwd } : dados,
          business_id:business_id
        });
      } else {
        await axios.post(`${API}/register-user`, {
          business_id,
          formAddUser: { ...dados, pwd: formAddProf.pwd },
        });
      }
      handleClose();
      getProfissionais();
    } catch (error) {
      setErroProf(mensagemErro(error, "Erro ao salvar profissional."));
    } finally {
      setSalvando(false);
    }
  };

  /* ----- salvar serviço (criar ou editar) ----- */
  const salvarServ = async () => {
    const erro = validarServ(formAddServ);
    if (erro) {
      setErroServ(erro);
      return;
    }

    setSalvando(true);
    setErroServ("");
    const dados = {
      name: formAddServ.name.trim(),
      timer: minutos(formAddServ.timer),   // duração em minutos
      price: parsePreco(formAddServ.price),
    };

    try {
      if (editServId !== null) {
        await axios.post(`${API}/update-service`, { id: editServId, formEditService: dados, business_id:business_id });
      } else {
        await axios.post(`${API}/register-service`, { business_id, formAddService: dados });
      }
      handleClose();
      getServicos();
    } catch (error) {
      setErroServ(mensagemErro(error, "Erro ao salvar serviço."));
    } finally {
      setSalvando(false);
    }
  };

  /* ----- salvar produto (criar ou editar) ----- */
  const salvarProd = async () => {
    const erro = validarProd(formAddProd);
    if (erro) {
      setErroProd(erro);
      return;
    }

    setSalvando(true);
    setErroProd("");
    const dados = {
      name: formAddProd.name.trim(),
      price: parsePreco(formAddProd.price),
    };

    try {
      if (editProdId !== null) {
        await axios.post(`${API}/update-product`, { id: editProdId, formEditProd: dados, business_id:business_id });
      } else {
        await axios.post(`${API}/register-product`, { business_id, formAddProd: dados });
      }
      handleClose();
      getProdutos();
    } catch (error) {
      setErroProd(mensagemErro(error, "Erro ao salvar produto."));
    } finally {
      setSalvando(false);
    }
  };

  const desativar = async ({ rota, id, descricao, setErro, recarregar }) => {
    if (!ehAdmin || salvando) return;
    if (!window.confirm(`Desativar ${descricao}?\n\nEle deixará de aparecer nas listas e não poderá mais receber agendamentos.`)) return;

    const enviar = (force) => axios.post(`${API}/${rota}`, { id, force });

    setSalvando(true);
    setErro("");
    try {
      try {
        await enviar(false);
      } catch (error) {
        if (error.response?.status !== 409) throw error;
        // tem agendamentos futuros: só segue se o admin confirmar
        if (!window.confirm(`${error.response.data.message}\n\nDesativar mesmo assim?`)) return;
        await enviar(true);
      }
      handleClose();
      recarregar();
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível desativar."));
    } finally {
      setSalvando(false);
    }
  };

  const diasOrdenados = [...formAddProf.work_days].sort((a, b) => a - b);
  // mantém o cargo atual na lista mesmo que não esteja em CARGOS
  const opcoesCargo = [...new Set([...CARGOS, formAddProf.cargo].filter(Boolean))];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <div className="container-ag">
        <div className="content-ag">
          <div className="sideMenu">
            <div className="sideMenu-content">
              <div className="sideMenu-orientation">
                <div className="sideMenu-top">
                  <img src="/Images/bot-3d.png" className="sideMenu-bot3d" alt="" />
                  <h3 className="sideMenu-name">{nome}</h3>
                </div>
                <div className="sideMenu-btns">
                  <Link className="sideMenu-option" to="/Agendamentos">
                    <img src="/Images/calendar.svg" className="sideMenu-btn-icon" alt="" />
                    <span className="sideMenu-title">Agendamentos</span>
                  </Link>
                  {ehAdmin && (
                    <>
                    <Link className="sideMenu-option-active" to="/Configuracoes">
                      <img src="/Images/options.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Configuração</span>
                    </Link>
                    <Link className="sideMenu-option" to="/Lancamentos">
                      <img src="/Images/bag.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Lançamentos</span>
                    </Link>
                    </>
                  )}
                </div>
              </div>
              <div className="sideMenu-logout">
                <div className="sideMenu-logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  <img src="/Images/logout.svg" className="sideMenu-logout-icon" alt="" />
                  <span className="sideMenu-title">Sair</span>
                </div>
                <img className="sideMenu-logo" src="/Images/sigmaBranco.png" alt="Sigma Systems" />
              </div>
            </div>
          </div>

          <div className="content">
            <div className="content-top">
              <h1 className="content-title">Configurações</h1>
            </div>
            <div className="content-block">
              <div className="config-header">
                <h2 className="config-title">Profissionais</h2>
                {ehAdmin ? (<img onClick={handleOpenProf} className="config-plus" src="/Images/plus.svg" alt="Adicionar profissional" />):('')}
              </div>
              <Grid2 container spacing={1} sx={{ width: "100%", mb: "10px", mt: "10px" }}>
                {profissionais.map((p) => (
                  <Grid2 key={p.id}>
                    <div className="config-option" {...clicavel(() => abrirEdicaoProf(p))}>
                      {p.name}{p.role ? ` · ${p.role}` : ""}
                    </div>
                  </Grid2>
                ))}
              </Grid2>

              <div className="config-header">
                <h2 className="config-title">Serviços</h2>
                {ehAdmin ? (<img onClick={handleOpenServ} className="config-plus" src="/Images/plus.svg" alt="Adicionar serviço" />):('')}
              </div>
              <Grid2 container spacing={1} sx={{ width: "100%", mb: "10px", mt: "10px" }}>
                {services.map((servico) => (
                  <Grid2 key={servico.id}>
                    <div className="config-option" {...clicavel(() => abrirEdicaoServ(servico))}>
                      {servico.name} · {formatarDuracao(servico.timer)} · {formatarPreco(servico.price)}
                    </div>
                  </Grid2>
                ))}
              </Grid2>

              <div className="config-header">
                <h2 className="config-title">Produtos</h2>
                {ehAdmin ? (<img onClick={handleOpenProd} className="config-plus" src="/Images/plus.svg" alt="Adicionar produto" />):('')}
              </div>
              <Grid2 container spacing={1} sx={{ width: "100%", mb: "10px", mt: "10px" }}>
                {produtos.map((produto) => (
                  <Grid2 key={produto.id}>
                    <div className="config-option" {...clicavel(() => abrirEdicaoProd(produto))}>
                      {produto.name} · {formatarPreco(produto.price)}
                    </div>
                  </Grid2>
                ))}
              </Grid2>
            </div>
          </div>
        </div>

        {/* ===== Modal: profissional ===== */}
        <Modal open={open} onClose={handleClose}>
          <Box sx={style}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/person.svg" alt="" />
                  <span className="modal-title">
                    {editProfId !== null ? "Editar Profissional" : "Adicionar Profissional"}
                  </span>
                </div>
                <div className="modal-header2">
                  {editProfId !== null && (
                    <img src="/Images/trash.svg" className="modal-trash-icon" alt="Desativar profissional"
                      title="Desativar profissional"
                      onClick={() => desativar({
                        rota: "deactivate-user", id: editProfId,
                        descricao: `o profissional ${formAddProf.nome}`,
                        setErro: setErroProf, recarregar: getProfissionais,
                      })} />
                  )}
                </div>
              </div>
              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Nome" fullWidth variant="outlined" color="success"
                      value={formAddProf.nome}
                      onChange={(e) => handleChangeAddProf("nome", e.target.value)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Cargo</InputLabel>
                      <Select label="Cargo" value={formAddProf.cargo}
                        MenuProps={{ style: { maxHeight: 360 } }}
                        onChange={(e) => handleChangeAddProf("cargo", e.target.value)}>
                        {opcoesCargo.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Serviços</InputLabel>
                      <Select label="Serviços" multiple
                        input={<OutlinedInput label="Serviços" />}
                        MenuProps={{ style: { maxHeight: 360 } }}
                        value={formAddProf.servicos}
                        onChange={(e) => handleChangeAddProf("servicos", e.target.value)}>
                        {services.length === 0 && (
                          <MenuItem disabled value="">Cadastre um serviço primeiro</MenuItem>
                        )}
                        {services.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Permissão</InputLabel>
                      <Select label="Permissão" value={formAddProf.type}
                        onChange={(e) => handleChangeAddProf("type", e.target.value)}>
                        <MenuItem value="ADMIN">Admin</MenuItem>
                        <MenuItem value="EMPLOYEE">Funcionário</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Comissão em %" fullWidth variant="outlined" color="success"
                      type="number" slotProps={{ htmlInput: { min: 0, max: 100, step: "0.01" } }}
                      value={formAddProf.comission}
                      onChange={(e) => handleChangeAddProf("comission", e.target.value)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Email" type="email" fullWidth variant="outlined" color="success"
                      value={formAddProf.email}
                      onChange={(e) => handleChangeAddProf("email", e.target.value)} />
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success" variant="outlined">
                      <InputLabel htmlFor="senha-prof">
                        {editProfId !== null ? "Nova senha (opcional)" : "Senha"}
                      </InputLabel>
                      <OutlinedInput
                        id="senha-prof"
                        label={editProfId !== null ? "Nova senha (opcional)" : "Senha"}
                        autoComplete="new-password"
                        type={showPassword ? "text" : "password"}
                        value={formAddProf.pwd}
                        onChange={(e) => handleChangeAddProf("pwd", e.target.value)}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton aria-label="mostrar ou ocultar senha" edge="end"
                              onClick={() => setShowPassword((s) => !s)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      {editProfId !== null && (
                        <FormHelperText>Deixe em branco para manter a senha atual</FormHelperText>
                      )}
                    </FormControl>
                  </Grid2>

                  {/* ----- dias e horários (manhã e tarde em cada dia) ----- */}
                  <Grid2 sx={{ width: "100%" }}>
                    <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Dias de atendimento</div>
                    <ToggleButtonGroup fullWidth size="small" color="success"
                      value={formAddProf.work_days} onChange={handleDias}>
                      {DIAS.map((d) => (
                        <ToggleButton key={d.v} value={d.v}>{d.l}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid2>

                  {diasOrdenados.length > 0 && (
                    <Grid2 sx={{ width: "100%", mt: 1 }}>
                      <div style={{ ...gradeLinha, fontSize: 13, color: "#666", marginBottom: 6 }}>
                        <span />
                        <span style={{ gridColumn: "2 / 4" }}>Manhã</span>
                        <span style={{ gridColumn: "4 / 6" }}>Tarde</span>
                        <span />
                      </div>
                      {diasOrdenados.map((dia) => (
                        <LinhaDia key={dia} dia={dia}
                          horario={formAddProf.horarios[dia] ?? HORARIO_VAZIO}
                          onChange={handleChangeHorario}
                          onCopiar={copiarParaTodos} />
                      ))}
                    </Grid2>
                  )}
                </Grid2>

                {erroProf && <Alert severity="error" sx={{ mt: 2 }}>{erroProf}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarProf} disabled={salvando} sx={btnPrimario}>
                    {editProfId !== null ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={handleClose} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>

        {/* ===== Modal: serviço ===== */}
        <Modal open={openAddServ} onClose={handleClose}>
          <Box sx={style}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/tesoura.svg" alt="" />
                  <span className="modal-title">
                    {editServId !== null ? "Editar Serviço" : "Criar Serviço"}
                  </span>
                </div>
                <div className="modal-header2">
                  {editServId !== null && (
                    <img src="/Images/trash.svg" className="modal-trash-icon" alt="Desativar serviço"
                      title="Desativar serviço"
                      onClick={() => desativar({
                        rota: "deactivate-service", id: editServId,
                        descricao: `o serviço ${formAddServ.name}`,
                        setErro: setErroServ, recarregar: getServicos,
                      })} />
                  )}
                </div>
              </div>
              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Nome" fullWidth variant="outlined" color="success"
                      value={formAddServ.name}
                      onChange={(e) => handleChangeAddServ("name", e.target.value)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <CampoHora label="Tempo" value={formAddServ.timer}
                      onChange={(v) => handleChangeAddServ("timer", v)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel htmlFor={`${outlinedAmountId}-input`}>Valor</InputLabel>
                      <OutlinedInput
                        id={`${outlinedAmountId}-input`}
                        label="Valor"
                        inputMode="decimal"
                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                        value={formAddServ.price}
                        onChange={(e) => handleChangeAddServ("price", e.target.value)}
                      />
                    </FormControl>
                  </Grid2>
                </Grid2>

                {erroServ && <Alert severity="error" sx={{ mt: 2 }}>{erroServ}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarServ} disabled={salvando} sx={btnPrimario}>
                    {editServId !== null ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={handleClose} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>

        {/* ===== Modal: produto ===== */}
        <Modal open={openAddProd} onClose={handleClose}>
          <Box sx={style}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/tesoura.svg" alt="" />
                  <span className="modal-title">
                    {editProdId !== null ? "Editar Produto" : "Criar Produto"}
                  </span>
                </div>
                <div className="modal-header2">
                  {editProdId !== null && (
                    <img src="/Images/trash.svg" className="modal-trash-icon" alt="Desativar produto"
                      title="Desativar produto"
                      onClick={() => desativar({
                        rota: "deactivate-product", id: editProdId,
                        descricao: `o produto ${formAddProd.name}`,
                        setErro: setErroProd, recarregar: getProdutos,
                      })} />
                  )}
                </div>
              </div>
              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Nome" fullWidth variant="outlined" color="success"
                      value={formAddProd.name}
                      onChange={(e) => handleChangeAddProd("name", e.target.value)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel htmlFor={`${outlinedAmountId}-prod`}>Valor</InputLabel>
                      <OutlinedInput
                        id={`${outlinedAmountId}-prod`}
                        label="Valor"
                        inputMode="decimal"
                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                        value={formAddProd.price}
                        onChange={(e) => handleChangeAddProd("price", e.target.value)}
                      />
                    </FormControl>
                  </Grid2>
                </Grid2>

                {erroProd && <Alert severity="error" sx={{ mt: 2 }}>{erroProd}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarProd} disabled={salvando} sx={btnPrimario}>
                    {editProdId !== null ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={handleClose} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </LocalizationProvider>
  );
}

export default Configuracao;
